// Transient feedback toast for the Cleanup inline-edit flow.
//
// Fix #6 (Nate, 2026-07-06): saving a contact from the Cleanup page's
// "Safe to Bulk Resolve" section gave NO confirmation — no toast, no
// sign the record saved or the issue cleared. The backend already
// reconciles the issue synchronously on the PATCH; the user just never
// saw it. This is the missing signal.
//
// Deliberately self-contained: there's no app-wide toast provider and
// this fix shouldn't add one. The toast is a CONTROLLED component — the
// caller owns the message; this renders it (portaled to <body> so it
// floats above the record drawer and any overlay) and auto-dismisses.
// The visual style mirrors the DecideRail "Done — {verb}" success card
// so Cleanup feedback reads as one system.

import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Check } from "lucide-react";

export type CleanupToastTone = "success" | "neutral";

export type CleanupToastMessage = {
  text: string;
  tone: CleanupToastTone;
};

// Message for a completed inline save. `changed` is false for a no-op
// save (the draft equalled the record) so we never claim an update that
// didn't actually happen — the steward still gets feedback that the save
// was received, just honest about what it did.
export function savedToast(
  entity: "contact" | "household",
  changed: boolean,
): CleanupToastMessage {
  const noun = entity === "household" ? "household" : "contact";
  return changed
    ? { text: `Saved — ${noun} updated`, tone: "success" }
    : { text: "No changes to save", tone: "neutral" };
}

// Message shown when a save cleared the cleanup issue (the row left the
// queue). Surfaced by the host that owns the issue list, after the
// post-save reload shows the issue is gone — no extra backend call.
export function resolvedToast(
  entity: "contact" | "household",
): CleanupToastMessage {
  const noun = entity === "household" ? "household" : "contact";
  return { text: `Resolved ✓ — ${noun} updated and the issue is cleared`, tone: "success" };
}

export function CleanupToastHost({
  toast,
  onDismiss,
  durationMs = 4000,
}: {
  toast: CleanupToastMessage | null;
  onDismiss: () => void;
  durationMs?: number;
}) {
  // Hold onDismiss in a ref so the auto-dismiss timer restarts ONLY when
  // a new toast is set (object identity changes) — not on every parent
  // render that hands us a fresh inline callback.
  const dismissRef = useRef(onDismiss);
  dismissRef.current = onDismiss;

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => dismissRef.current(), durationMs);
    return () => clearTimeout(t);
  }, [toast, durationMs]);

  if (!toast) return null;

  const success = toast.tone === "success";
  return createPortal(
    <div
      role="status"
      aria-live="polite"
      onClick={onDismiss}
      style={{
        position: "fixed",
        bottom: 24,
        right: 24,
        zIndex: 2000,
        maxWidth: 360,
        display: "flex",
        alignItems: "center",
        gap: 8,
        background: success ? "var(--success-bg, #e8f5ec)" : "var(--bg-0)",
        border: `1px solid ${success ? "var(--success)" : "var(--mid-gray)"}`,
        borderRadius: 10,
        padding: "11px 14px",
        fontSize: 13,
        fontWeight: 700,
        color: "var(--navy)",
        boxShadow: "0 6px 20px rgba(2,28,49,0.18)",
        cursor: "pointer",
      }}
    >
      {success && (
        <Check
          size={16}
          strokeWidth={2.5}
          color="var(--success)"
          aria-hidden
        />
      )}
      {toast.text}
    </div>,
    document.body,
  );
}
